import { CircleAlert, Lock } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";
import { Skeleton } from "../primitives/card";
import { ActionLink } from "./action-link";
import { CopyButton } from "./copy";
import { StageBadge } from "./status";

/**
 * Empty state (ui-spec §4.3, dashboard theme): one line and the scoped CLI command, centred in a dashed frame where
 * the content would be; a short reason or an action only where the page needs one. Inside a table card the frame
 * is dropped, since the card already draws one.
 */
export function EmptyState({
  title,
  description,
  actions,
  cli,
  cliNote,
  className,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  cli?: string;
  /** "Run from your project folder" when the command uploads the current directory. */
  cliNote?: string;
  className?: string;
}) {
  return (
    <div data-testid="empty-state" className={cn("flex flex-col items-center rounded-lg border border-dashed border-line px-6 py-12 text-center", className)}>
      <h2 className="text-card-title-compact font-semibold text-fg-primary">{title}</h2>
      {description ? <p className="mt-1.5 max-w-[460px] text-ui text-fg-muted">{description}</p> : null}
      {cli ? (
        <div className="mt-5 w-full max-w-[640px]">
          {cliNote ? <p className="mb-1 text-small text-fg-muted">{cliNote}</p> : null}
          <div className="flex items-center gap-2 rounded-md border border-line bg-sunken py-1 pr-1 pl-3 text-left">
            <span aria-hidden className="font-mono text-mono text-fg-faint select-none">$</span>
            <code tabIndex={0} className="min-w-0 flex-1 overflow-x-auto font-mono text-mono whitespace-nowrap text-fg-body">{cli}</code>
            <CopyButton text={cli} label="Copy command" />
          </div>
        </div>
      ) : null}
      {actions ? <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface ErrorView {
  title: string;
  reason?: string;
  message?: string;
  fix?: string;
  docsHref?: string;
  requestId?: string;
}

/** kubectl-style error (ADR-028): summary, reason, fix, docs, request id with Copy; Retry where it is safe. */
export function ErrorState({ error, onRetry, className }: { error: ErrorView; onRetry?: () => void; className?: string }) {
  return (
    <div role="alert" data-testid="error-state" className={cn("rounded-lg border border-line bg-card p-4", className)}>
      <div className="flex items-start gap-3">
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-dot" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-ui font-medium text-fg-primary">{error.title}</h2>
          {error.message ? <p className="mt-1 text-ui text-fg-secondary">{error.message}</p> : null}
          {error.reason ? (
            <p className="mt-2 text-small text-fg-muted">
              Reason <code className="font-mono text-fg-secondary">{error.reason}</code>
            </p>
          ) : null}
          {error.fix ? <RecoveryInstructions text={error.fix} /> : null}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {onRetry ? (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            ) : null}
            {error.docsHref ? (
              <ActionLink href={error.docsHref} external>
                Docs
              </ActionLink>
            ) : null}
            {error.requestId ? (
              <span className="inline-flex items-center gap-1 text-small text-fg-muted">
                Request <code className="font-mono">{error.requestId}</code>
                <CopyButton text={error.requestId} label="Copy request id" />
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Render backtick-delimited recovery commands as selectable, copyable text, never executable markup. */
function RecoveryInstructions({ text }: { text: string }) {
  return <div className="mt-2 grid gap-2 text-ui text-fg-body">{text.split(/(`[^`]+`)/g).map((part, i) => {
    if (!part.startsWith("`") || !part.endsWith("`")) return part.trim() ? <p key={i}>{part}</p> : null;
    const code = part.slice(1, -1);
    return <div key={i} className="flex min-w-0 items-start gap-2 rounded-md border border-line bg-sunken py-2 pr-2 pl-3">
      <code tabIndex={0} className="min-w-0 flex-1 whitespace-pre-wrap break-words font-mono text-mono">{code}</code>
      <CopyButton text={code} label="Copy command" showLabel />
    </div>;
  })}</div>;
}

/** A gated area shows what access includes and how to ask, never a missing button (ui-spec §4.7). */
export function RequestAccess({
  stage,
  title,
  description,
  includes,
  onRequest,
  requested,
  docsHref,
}: {
  stage: "Alpha" | "Beta";
  title: string;
  description: React.ReactNode;
  includes?: string[];
  onRequest?: () => void;
  requested?: string;
  docsHref?: string;
}) {
  return (
    <div className="mx-auto flex max-w-[480px] flex-col items-center py-12 text-center">
      <Lock className="size-4 text-fg-muted" aria-hidden />
      <div className="mt-3 flex items-center gap-2">
        <h2 className="text-ui font-medium text-fg-primary">{title}</h2>
        <StageBadge stage={stage} />
      </div>
      <p className="mt-2 text-ui text-fg-secondary">{description}</p>
      {includes?.length ? (
        <ul className="mt-3 list-disc pl-5 text-left text-ui text-fg-secondary">
          {includes.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      ) : null}
      <div className="mt-5 flex items-center gap-4">
        {requested ? (
          <span className="text-ui text-fg-secondary">Requested · {requested}</span>
        ) : (
          <Button onClick={onRequest} disabled={!onRequest}>
            Request access
          </Button>
        )}
        {docsHref ? (
          <ActionLink href={docsHref} external>
            Docs
          </ActionLink>
        ) : null}
      </div>
    </div>
  );
}

/** Layout-matched list skeleton: the filter bar and five rows (ui-spec §4.2); the page header stays drawn. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" data-testid="list-skeleton">
      <Skeleton className="h-[var(--nd-control-height)] w-64 max-w-full" />
      <div className="mt-3 overflow-hidden rounded-lg border border-line">
        <div className="h-[var(--nd-table-header-height)] bg-subtle" />
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex h-[var(--nd-row-height)] items-center gap-6 border-t border-line-subtle px-3">
            <Skeleton className="h-3.5 w-48" />
            <div className="flex-1" />
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" data-testid="detail-skeleton">
      <Skeleton className="h-6 w-72 max-w-full" />
      <Skeleton className="mt-2 h-3.5 w-96 max-w-full" />
      <Skeleton className="mt-6 h-9 w-80 max-w-full" />
      <Skeleton className="mt-4 h-[50vh] w-full" />
    </div>
  );
}

/** A slim 2px progress bar kept at the top while the next route's critical query resolves. */
export function RouteProgress({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden
      className={cn(
        "fixed inset-x-0 top-0 z-[80] h-0.5 origin-left bg-accent-fg transition-transform duration-500",
        active ? "scale-x-75" : "scale-x-0",
      )}
    />
  );
}
